import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/site-data";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const light = scrolled; // when scrolled, we're over a white surface
  const dark = !scrolled; // when at top, we're over a dark hero

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        light
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <div className={`relative h-11 w-11 p-[2px] transition-colors ${light ? "bg-white" : "bg-white/95"} ring-1 ${light ? "ring-slate-200" : "ring-white/20"}`}>
              <img src="/cetc-logo.jpg" alt="Cutting Edge Technologies logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className={`font-display font-semibold text-[15px] tracking-tight transition-colors ${light ? "text-slate-900" : "text-white"}`}>
                Cutting Edge
              </span>
              <span className={`eyebrow transition-colors ${light ? "text-slate-500" : "text-emerald-400"}`}>Technologies Consulting</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium transition-colors ${
                    light
                      ? isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
                      : isActive ? "text-white" : "text-slate-300 hover:text-white"
                  }`
                }
                end={link.to === "/"}
              >
                {({ isActive }) => (
                  <span className="relative">
                    {link.label}
                    {isActive && (
                      <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-emerald-500" />
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-book-consultation"
              className={`group inline-flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                light
                  ? "bg-slate-900 text-white hover:bg-emerald-500 hover:text-slate-950"
                  : "bg-emerald-500 text-slate-950 hover:bg-emerald-400"
              }`}
            >
              Book Consultation
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
            </Link>
          </div>

          <button
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 ${light ? "text-slate-900" : "text-white"}`}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200">
          <nav className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `py-3 border-b border-slate-100 text-sm font-medium ${
                    isActive ? "text-emerald-600" : "text-slate-900"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-4 bg-slate-900 text-white text-center py-3 text-sm font-medium"
              data-testid="mobile-book-consultation"
            >
              Book Consultation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
